import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from '../../core/services/settings.service';
import { Supplier } from '../../core/models/settings.models';
import { confirmAction } from '../../core/utils/confirm.util';
import {
  emailValidationMessage,
  phoneValidationMessage,
} from '../../core/utils/input-validation.util';
import { PhoneOnlyDirective } from '../../core/directives';

@Component({
  selector: 'app-suppliers',
  standalone: true,
  imports: [FormsModule, PhoneOnlyDirective],
  templateUrl: './suppliers.component.html',
  styleUrls: ['../../settings-shared.css', './suppliers.component.css'],
})
export class SuppliersComponent implements OnInit {
  private readonly settings = inject(SettingsService);

  protected readonly showModal = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isEditing = computed(() => this.editingId() !== null);
  protected readonly showToast = signal(false);
  protected readonly toastMessage = signal('');

  protected supplierForm: Omit<Supplier, 'id'> = {
    code: '',
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    status: 'active',
  };

  protected readonly suppliers = this.settings.allSuppliers;

  ngOnInit(): void {
    this.settings.refreshFromApi();
  }

  openModal(): void {
    this.editingId.set(null);
    this.resetForm();
    this.showModal.set(true);
  }

  openEdit(row: Supplier): void {
    this.editingId.set(row.id);
    this.supplierForm = {
      code: row.code,
      name: row.name,
      contactPerson: row.contactPerson || '',
      email: row.email || '',
      phone: row.phone || '',
      status: row.status,
    };
    this.showModal.set(true);
  }

  closeModal(): void {
    this.showModal.set(false);
    this.editingId.set(null);
  }

  save(): void {
    const editingId = this.editingId();

    if (!this.supplierForm.name?.trim()) {
      this.toastMessage.set('Name is required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (!this.supplierForm.contactPerson) {
      this.toastMessage.set('Contact Person is required');
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    const emailError = emailValidationMessage(this.supplierForm.email, false);
    if (emailError) {
      this.toastMessage.set(emailError);
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    const phoneError = phoneValidationMessage(this.supplierForm.phone, false);
    if (phoneError) {
      this.toastMessage.set(phoneError);
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
      return;
    }

    if (editingId) {
      this.settings.updateSupplier(editingId, this.supplierForm).subscribe((result) => {
        this.toastMessage.set(result.message);
        this.showToast.set(true);
        setTimeout(() => this.showToast.set(false), 4000);
        if (result.success) {
          this.closeModal();
        }
      });
      return;
    }

    this.settings.addSupplier(this.supplierForm);
    this.toastMessage.set('Supplier added successfully');
    this.closeModal();
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  deleteRow(row: Supplier): void {
    if (!confirmAction(`Delete "${row.name}"? This cannot be undone.`)) {
      return;
    }

    this.settings.deleteSupplier(row.id).subscribe((result) => {
      this.toastMessage.set(result.message);
      this.showToast.set(true);
      setTimeout(() => this.showToast.set(false), 4000);
    });
  }

  private resetForm(): void {
    this.supplierForm = {
      code: '',
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      status: 'active',
    };
  }
}
